import { FC, useState } from "react";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { StandardModal } from "@design-system/Modal/Modal";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useUser } from "@hooks/useUser";
import { ClientEntitySchema } from "@common/schemas/client.schema";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { canDeleteEntity } from "@common/helpers/canDeleteEntity";
import { viewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { DeleteEntityModal } from "@components/entity/DeleteEntityModal";

interface Props {
    entity: Pick<ClientEntitySchema, "id" | "name" | "members" | "isBusiness">;
    clientId: string;
}

export const EntityActionsDropdown: FC<Props> = ({ entity, clientId }) => {
    const user = useUser();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const isPersonal = !entity.isBusiness;
    const primaryAdmin = entity?.members.find(
        (member) => member.role === UserWorkspaceRole.PRIMARY_ADMIN,
    );
    const currentMember = entity.members.find(
        (member) => member.userId === user.id,
    );
    const isViewOnly = currentMember?.role === UserWorkspaceRole.VIEW_ONLY;
    const canUserDelete = canDeleteEntity({
        userType: user.type,
        workspaceRole: currentMember?.role ?? null,
    });
    const canDelete = !isPersonal && canUserDelete;
    const deleteTooltip = isPersonal
        ? "Personal entities cannot be deleted."
        : viewOnlyTooltipReason({
              isViewOnly,
              specificReason: `Only the primary admin can delete an entity. ${
                  primaryAdmin?.email
                      ? `Primary admin of the entity is ${primaryAdmin?.email}`
                      : "No primary admin found."
              }`,
          });

    return (
        <>
            <CaptureClicks>
                <Menu.More>
                    <Menu.Item
                        destructive
                        disabled={!canDelete}
                        disabledReason={deleteTooltip}
                        onClick={() => setShowDeleteModal(true)}
                    >
                        Delete entity
                    </Menu.Item>
                </Menu.More>
            </CaptureClicks>

            <StandardModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                size="md"
            >
                <DeleteEntityModal
                    entity={{
                        id: entity.id,
                        name: entity.name,
                        workspaceId: clientId,
                    }}
                    onClose={() => setShowDeleteModal(false)}
                />
            </StandardModal>
        </>
    );
};
